import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    notifications: {
      messages: true,
      orders: true
    },
    appearance: {
      darkMode: false
    }
  });

  useEffect(() => {
    // Load settings from localStorage on mount
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(prev => ({
          ...prev,
          notifications: parsedSettings.notifications || prev.notifications,
          appearance: parsedSettings.appearance || prev.appearance
        }));
      } catch (error) {
        console.error('Error parsing saved settings:', error);
      }
    }

    const legacyDarkMode = localStorage.getItem('darkMode');
    if (legacyDarkMode !== null) {
      const enabled = legacyDarkMode === 'true';
      setSettings(prev => ({
        ...prev,
        appearance: {
          ...prev.appearance,
          darkMode: enabled
        }
      }));
    }
  }, []);

  useEffect(() => {
    const theme = settings.appearance.darkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('darkMode', String(settings.appearance.darkMode));
  }, [settings.appearance.darkMode]);

  const updateSetting = (category, key, value) => {
    setSettings(prev => {
      const newSettings = {
        ...prev,
        [category]: {
          ...(prev[category] || {}),
          [key]: value
        }
      };
      
      // Save to localStorage
      localStorage.setItem('userSettings', JSON.stringify(newSettings));
      
      return newSettings;
    });
  };

  const toggleNotification = (type) => {
    updateSetting('notifications', type, !settings.notifications[type]);
  };

  const toggleDarkMode = () => {
    updateSetting('appearance', 'darkMode', !settings.appearance.darkMode);
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      toggleNotification,
      toggleDarkMode,
      updateSetting
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
